import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { VyDatePicker } from '../new-design/date-picker';
import { formatAmount } from './format-amount';
import { SUM_TOLERANCE, type RevenuePoint } from './types';

type Props = {
  points: RevenuePoint[];
  sessionTotal: number;
  disabled: boolean;
  onPointsChange: (points: RevenuePoint[]) => void;
  mode?: 'single' | 'milestones';
};

export const PointInTimeEditor: React.FC<Props> = ({
  points,
  sessionTotal,
  disabled,
  onPointsChange,
  mode = 'milestones',
}) => {
  const isSingle = mode === 'single';
  const pointsSum = points.reduce((acc, point) => acc + point.amount, 0);
  const delta = pointsSum - sessionTotal;
  const sumMatches = Math.abs(delta) <= SUM_TOLERANCE;

  const updatePoint = (index: number, next: Partial<RevenuePoint>) => {
    onPointsChange(points.map((point, idx) => (idx === index ? { ...point, ...next } : point)));
  };

  const removePoint = (index: number) => {
    onPointsChange(points.filter((_, idx) => idx !== index));
  };

  const addPoint = () => {
    const fallbackDate = points.length > 0 ? points[points.length - 1].date : new Date();
    onPointsChange([...points, { date: fallbackDate, amount: 0 }]);
  };

  // Point-in-Time renders a single record: full value on one editable date.
  const visiblePoints = isSingle ? points.slice(0, 1) : points;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
      {visiblePoints.map((point, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {!isSingle && (
            <Typography variant="body2" sx={{ minWidth: 88, color: 'text.secondary' }}>
              Milestone {index + 1}
            </Typography>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <VyDatePicker
              value={point.date}
              onChange={(next) => {
                if (next) updatePoint(index, { date: next });
              }}
              disabled={disabled}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              type="number"
              value={isSingle ? sessionTotal : point.amount}
              onChange={(event) => updatePoint(index, { amount: Number(event.target.value) })}
              disabled={disabled}
              fullWidth
              slotProps={isSingle ? { input: { readOnly: true } } : undefined}
              data-vy={`product-inspect-dialog-point-${index}-amount`}
            />
          </Box>
          {!isSingle && (
            <Typography
              variant="body2"
              sx={{ minWidth: 56, color: 'text.secondary', textAlign: 'right' }}
            >
              {sessionTotal > 0 ? `${((point.amount / sessionTotal) * 100).toFixed(2)}%` : '—'}
            </Typography>
          )}
          {!isSingle && (
            <IconButton
              onClick={() => removePoint(index)}
              disabled={disabled}
              data-vy={`product-inspect-dialog-point-${index}-remove`}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ))}

      {!isSingle && (
        <Button
          startIcon={<AddIcon />}
          onClick={addPoint}
          disabled={disabled}
          sx={{ alignSelf: 'flex-start' }}
          data-vy="product-inspect-dialog-add-point"
        >
          Add point
        </Button>
      )}

      {!isSingle && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Points sum: {formatAmount(pointsSum)}
          </Typography>
          <Typography variant="body2" color={sumMatches ? 'success.main' : 'error.main'}>
            {sumMatches
              ? `Matches total (${formatAmount(sessionTotal)})`
              : `${delta > 0 ? 'Over' : 'Under'} by ${formatAmount(Math.abs(delta))} (total ${formatAmount(sessionTotal)})`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
